import { findIndex, isEmpty, isEqual, range, uniq } from 'lodash';

import { RenderEngine } from '../models/render-engine';
import { AddressConverter } from '../../address-converter';
import { Block } from '../models/block';
import { Styles } from '../models/styles';
import Tools from '../../tools';
import SpacerBlock from '../blocks/calendar/spacer-block';
import FlightBarWallSubTotalBlock from '../blocks/media-hierarchy/flight-bar-wall-sub-total-block';
import ValueBlock from '../blocks/right-hand-totals/value-block';
import FlightBarWallBarBlock from '../blocks/media-hierarchy/flight-bar-wall-bar-block';
import FlightBarWallInflightOverlayBlock from '../blocks/media-hierarchy/flight-bar-wall-inflight-overlay-block';
import TotalBlock from '../blocks/right-hand-totals/total-block';
import GTValueBlock from '../blocks/grand-totals/value-block';
import { inferExcelNumberFormat } from '../helpers/excel-format-helper';
export type CombinedStyle = {
    styles: Styles;
    ranges: string[];
};

export class ExcelRenderer extends RenderEngine {
    ac = new AddressConverter();
    isGettingRendered: boolean = false;

    setupWorksheet = async (
        context: Excel.RequestContext,
        name: string = 'Report',
        deleteSheet: boolean = false
    ): Promise<Excel.Worksheet> => {
        let app = context.workbook.application;
        let sheet = context.workbook.worksheets.getItemOrNullObject(name);

        await context.sync();

        if (sheet.isNullObject) {
            sheet = context.workbook.worksheets.add(name);
        }

        if (deleteSheet) {
            sheet.delete();
            sheet = context.workbook.worksheets.add(name);
        }

        await context.sync();

        sheet.activate();
        app.suspendScreenUpdatingUntilNextSync();

        return sheet;
    };

    setupStylesheet = async (
        context: Excel.RequestContext,
        name: string = 'Styles'
    ): Promise<Excel.Worksheet> => {
        let app = context.workbook.application;
        let sheet = context.workbook.worksheets.getItemOrNullObject(name);

        await context.sync();

        if (sheet.isNullObject) {
            sheet = context.workbook.worksheets.add(name);
        }

        sheet.visibility = Excel.SheetVisibility.hidden;

        app.suspendScreenUpdatingUntilNextSync();

        return sheet;
    };

    render = async (
        blockRows: Block[][],
        {
            name,
            levelName,
            columnStart = 0,
            rowStart = 0,
            deleteSheet,
            weekly,
            precisedValue,
            isDecimal,
            setCurrentExcelContext,
            onWorkSheetFormatChanged
        }: {
            name?: string;
            levelName?: string;
            columnStart?: number;
            rowStart?: number;
            deleteSheet?: boolean;
            weekly?: boolean;
            precisedValue?: boolean;
            isDecimal?: boolean;
            setCurrentExcelContext?: (x: Excel.RequestContext) => void;
            onWorkSheetFormatChanged?: (event: Excel.WorksheetFormatChangedEventArgs) => Promise<any>;
        } = {}
    ) => {
        const start = Date.now();

        if (this.isGettingRendered) {
            return;
        }

        const value = sessionStorage.getItem('isGettingRendered');
        if (value && value === "true") {
            return;
        }

        this.isGettingRendered = true;

        try {
            let combinedStyles: CombinedStyle[] = [];
            let combinedValueStyles: CombinedStyle[] = [];
            let mergeRanges = [];
            await Excel.run(async (context) => {
                const numberFormatRanges = new Map<string, { format: string, length: number }>();
                const worksheet = await this.setupWorksheet(
                    context,
                    name,
                    deleteSheet
                );
                worksheet.onFormatChanged.remove(onWorkSheetFormatChanged);
                const stylesheet = await this.setupStylesheet(context);

                let imageData = [];
                let currentRow = rowStart;
                const currentColumnRow: {
                    [key: string]: { value: number; touched: boolean };
                } = {};
                blockRows.map((rows, rowIndex) => {
                    currentRow += 1;

                    let currentColumn = columnStart;
                    rows.map(async (block, columnIndex) => {
                        const {
                            columnStart: blockColumnStart,
                            rowStart: blockRowStart,
                            columnLength,
                            rowLength,
                            columnAfter,
                            rowAfter,
                            merge,
                            containsImage,
                            imageMaxHeight,
                            autofitColumns,
                        } = block;
                        currentColumn += 1;

                        // Set current column row index
                        const currentColumnRowIndex =
                            currentColumn + blockColumnStart;

                        // Set minimum column row
                        let minColumnRow;
                        Array.from({ length: columnLength }).forEach(
                            (_c, columnIndex) => {
                                const finalColumnRowIndex =
                                    currentColumnRowIndex + columnIndex;

                                if (
                                    currentColumnRow[finalColumnRowIndex] !==
                                    undefined &&
                                    (minColumnRow === undefined ||
                                        currentColumnRow[finalColumnRowIndex]
                                            .value > minColumnRow)
                                ) {
                                    minColumnRow =
                                        currentColumnRow[finalColumnRowIndex].value;
                                }
                            }
                        );

                        // Set current column row start
                        Array.from({ length: columnLength }).forEach(
                            (_c, columnIndex) => {
                                const finalColumnRowIndex =
                                    currentColumnRowIndex + columnIndex;

                                if (
                                    currentColumnRow[finalColumnRowIndex] ===
                                    undefined
                                ) {
                                    currentColumnRow[finalColumnRowIndex] = {
                                        value:
                                            minColumnRow !== undefined
                                                ? minColumnRow + blockRowStart
                                                : blockRowStart,
                                        touched: true,
                                    };
                                } else {
                                    if (minColumnRow !== undefined) {
                                        currentColumnRow[
                                            finalColumnRowIndex
                                        ].value = minColumnRow;
                                    }
                                    currentColumnRow[finalColumnRowIndex].value +=
                                        blockRowStart;
                                    currentColumnRow[finalColumnRowIndex].touched =
                                        true;
                                }
                            }
                        );

                        // Get range address
                        const { text: rangeStart } = this.ac.getAddressFromCoords(
                            currentColumn + blockColumnStart,
                            currentRow +
                            currentColumnRow[currentColumnRowIndex].value
                        );
                        currentColumn += blockColumnStart + columnLength - 1;
                        const { text: rangeEnd } = this.ac.getAddressFromCoords(
                            currentColumn,
                            currentRow +
                            currentColumnRow[currentColumnRowIndex].value +
                            rowLength -
                            1
                        );
                        const rangeAddress = `${rangeStart}:${rangeEnd}`;

                        // Get value data
                        // Todo SG: Check isSame
                        const { isEmpty, isSame, values } =
                            this.getValueData(block);

                        // Get styles
                        // Todo SG: Check style implementation

                        // Merge style from general to specific definition.
                        const generalStyle = Tools.deepCopy(
                            block.getGeneralStyles()
                        );
                        const componentStyle = Tools.deepCopy(
                            block.getComponentStyles()
                        );
                        const directStyles = Tools.deepCopy(
                            block.getDirectStyles()
                        );
                        const legendStyles = Tools.deepCopy(
                            block.getLegendStyles()
                        );

                        if (!(block instanceof SpacerBlock)) {
                            let mergedStyle = {};

                            mergedStyle = Tools.mergeObjects(
                                generalStyle,
                                componentStyle
                            );

                            mergedStyle = Tools.mergeObjects(
                                mergedStyle,
                                legendStyles
                            );

                            mergedStyle = Tools.mergeObjects(
                                mergedStyle,
                                directStyles
                            );
                            const metricBlock = (block instanceof FlightBarWallSubTotalBlock) || (block instanceof ValueBlock) || (block instanceof FlightBarWallBarBlock);
                            let styles = precisedValue || isDecimal || !metricBlock ? block.getStyles() : {
                                ...block.getStyles(),
                                columnWidth: block instanceof ValueBlock ?
                                    Math.max(
                                        block.value?.toString().length * 12,  // Increased multiplier for better spacing
                                        (block.getValue()?.toString() || '').length * 12  // Account for actual values
                                    ) : 50
                            };
                            mergedStyle = Tools.mergeObjects(
                                mergedStyle,
                                styles,
                            );

                            block.styles = mergedStyle;
                        }


                        // commented as casuing issue in formatting
                        if (!isEmpty) {
                            combinedValueStyles = this.combineStyles(
                                block,
                                rangeAddress,
                                combinedValueStyles
                            );
                        } else {
                            combinedStyles = this.combineStyles(
                                block,
                                rangeAddress,
                                combinedStyles
                            );
                        }

                        // Mark block for merge if needed
                        if (merge && (columnLength > 1 || rowLength > 1)) {
                            mergeRanges.push(rangeAddress);
                        }

                        // Only render values, if not empty
                        if (!isEmpty) {
                            // Create range
                            let range = worksheet.getRange(rangeAddress);

                            // Set values
                            if (!containsImage) {
                                range.values = values;
                                const isMetricBlock = block instanceof FlightBarWallSubTotalBlock
                                    || block instanceof ValueBlock
                                    || block instanceof FlightBarWallBarBlock
                                    || block instanceof FlightBarWallInflightOverlayBlock
                                    || block instanceof TotalBlock
                                    || block instanceof GTValueBlock;
                                if (isMetricBlock && values.length > 0 && values[0].length > 0) {
                                    try {
                                        const inferredFormat = inferExcelNumberFormat(values[0][0]);
                                        numberFormatRanges.set(rangeAddress, { format: inferredFormat, length: values.length });

                                    } catch (e) {
                                        console.log('error while fetching the formatting')
                                    }

                                }
                            } else {
                                //passing of the image content into an object
                                const { content, width, height } = JSON.parse(values[0][0]);
                                const scaledWidth = width * (1 + 1 / 100);
                                const scaledHeight = height * (1 + 1 / 100);
                                //to scale after the image is drawn
                                const img = worksheet.shapes.addImage(content);
                                img.width = scaledWidth;
                                img.height = scaledHeight;
                                const { text: imageRangeEnd } =
                                    // Todo SG + ZZ: Adapt ranges (assume that it no longer works correctly with my update)
                                    this.ac.getAddressFromCoords(
                                        currentColumn,
                                        currentRow +
                                        blockRowStart +
                                        imageMaxHeight -
                                        1
                                    );

                                const imageRangeAddress = `${rangeStart}:${imageRangeEnd}`;
                                img.load();

                                imageData.push({
                                    rangeAddress: imageRangeAddress,
                                    img,
                                });

                            }
                            // Auto fit columns
                            autofitColumns && range.format.autofitColumns();
                            range.format.autofitRows();
                            range.untrack();
                        }

                        // Update current column and current column row
                        currentColumn += columnAfter;
                        currentColumnRow[currentColumnRowIndex].value +=
                            rowLength - 1 + rowAfter;
                    });

                    // Correct untouched columnRow values
                    Object.entries(currentColumnRow).forEach(([, columnRow]) => {
                        if (!columnRow.touched) {
                            columnRow.value -= 1;
                            if (columnRow.value < 0) columnRow.value = 0;
                        } else {
                            columnRow.touched = false;
                        }
                    });
                });

                combinedStyles.forEach(({ styles, ranges }) => {
                    if (ranges.length) {
                        const firstRange = `A1:A1`;
                        let range = stylesheet.getRange(firstRange);

                        range.delete(Excel.DeleteShiftDirection.up);
                        range = stylesheet.getRange(firstRange);
                        range.setCellProperties([[{ format: { ...styles } }]]);

                        const targetRanges = worksheet.getRanges(ranges.join(','));
                        targetRanges.copyFrom(
                            `Styles!${firstRange}`,
                            Excel.RangeCopyType.formats
                        );

                        targetRanges.format.columnWidth = styles?.columnWidth;
                        targetRanges.format.rowHeight = styles?.rowHeight;
                        targetRanges.format.wrapText = styles?.wrapText;

                        // Add untrack for the combined target ranges
                        targetRanges.untrack();
                        range.untrack();
                    }
                });

                combinedValueStyles.forEach(({ styles, ranges }) => {
                    if (ranges.length) {
                        const firstRange = `A1:A1`;
                        let range = stylesheet.getRange(firstRange);

                        range.delete(Excel.DeleteShiftDirection.up);
                        range = stylesheet.getRange(firstRange);
                        range.setCellProperties([[{ format: { ...styles } }]]);

                        const targetRanges = worksheet.getRanges(ranges.join(','));
                        targetRanges.copyFrom(
                            `Styles!${firstRange}`,
                            Excel.RangeCopyType.formats
                        );

                        targetRanges.format.columnWidth = styles?.columnWidth;
                        targetRanges.format.rowHeight = styles?.rowHeight;
                        targetRanges.format.wrapText = styles?.wrapText;

                        // Add untrack for the combined target ranges
                        targetRanges.untrack();
                        range.untrack();
                    }
                });

                // Apply merges
                mergeRanges.forEach((rangeAddress) => {
                    const range = worksheet.getRange(rangeAddress);
                    range.merge(false);
                    range.untrack();
                });

                // Prepare number format operations
                //const numberFormatOperations: { range: Excel.Range; format: string }[] = [];
                numberFormatRanges.forEach((format, rangeAddress) => {
                    const range = worksheet.getRange(rangeAddress);
                    const formats: string[][] = Array(1).fill(null).map(() =>
                        Array(format.length).fill(format.format)
                    );
                    range.numberFormat = formats;
                    range.untrack();
                });


                // 1. get the image range position, now that all rows and columns have their final size
                const images = imageData.map(({ img, rangeAddress }) => {
                    const imageRange = worksheet.getRange(rangeAddress);
                    imageRange.load();
                    return { img: img as Excel.Shape, range: imageRange as Excel.Range, defaultWidthPercentage: 4, defaultHeightPercentage: 4 };
                });
                // 2. ensure that (range)data can be accessed
                await context.sync();

                // 3. scale and draw all images
                this.drawImages(images);

                const end = Date.now();
                // console.log(`EXCEL RENDER DURATION: ${end - start}ms`);
                setCurrentExcelContext && setCurrentExcelContext(context);
            });
        } catch (e) {
            console.log('Rendering session:', e);
        } finally {
            this.isGettingRendered = false;
            sessionStorage.setItem('isGettingRendered', "false");
        }
    };

    merge = (dst, src) => {
        Object.keys(src).forEach((key) => {
            if (!dst[key]) {
                dst[key] = src[key];
            } else if (
                typeof src[key] === 'object' &&
                src[key] !== null &&
                typeof dst[key] === 'object' &&
                dst[key] !== null
            ) {
                this.merge(dst[key], src[key]);
            }
        });
    };

    drawImages = async (
        images: {
            img: Excel.Shape;
            range: Excel.Range;
            defaultWidthPercentage: number;
            defaultHeightPercentage: number;
        }[]
    ) => {
        images.forEach(async ({ img, range, defaultWidthPercentage, defaultHeightPercentage }, index) => {
            //Interpretation of input 1 to 1% of image width and height increase 
            const scaleFactor = Math.trunc(9 + defaultWidthPercentage / 100);

            // Calculate scaled width and height
            const truncatedWidth = Math.floor(img.width);
            const scaledWidth = Math.floor(truncatedWidth * scaleFactor - 4) || defaultWidthPercentage;
            const truncatedHeight = Math.floor(img.height);
            const scaledHeight = Math.floor(truncatedHeight * scaleFactor - 4) || defaultHeightPercentage;

            // Set position for the image
            const left = range.left;
            const top = range.top;

            img.set({
                name: `image${index}`,
                left: left,
                top: top,
                width: scaledWidth,
                height: scaledHeight,
            });
        });
    };

    combineStyles = (
        block: Block,
        range: string,
        combinedStyles: CombinedStyle[] = []
    ): CombinedStyle[] => {
        const finalCombinedStyles = [...combinedStyles];

        const address = this.ac.getRangeFromString(range);
        const coords = this.ac.getCoordsFromAddress(
            address.start.column,
            address.start.row
        );

        this.getStyles(block).forEach((rows, rowIndex) => {
            // Check if all styles of the block are the same
            let isSame = true;
            let lastStyles = null;
            rows.forEach((styles) => {
                if (isSame) {
                    if (lastStyles && !isEqual(lastStyles, styles))
                        isSame = false;

                    lastStyles = styles;
                }
            });

            // If all styles of the block are the same, combine style for complete range
            if (isSame) {
                // modified find index condition for FB-291 
                let stylesIndex = findIndex(finalCombinedStyles, (combinedStyles) =>
                    combinedStyles.styles == lastStyles
                );
                if (stylesIndex === -1) {
                    finalCombinedStyles.push({
                        styles: lastStyles,
                        ranges: [range],
                    });
                    stylesIndex = finalCombinedStyles.length - 1;
                } else {
                    finalCombinedStyles[stylesIndex].ranges.push(range);
                }

                finalCombinedStyles[stylesIndex].ranges = uniq(
                    finalCombinedStyles[stylesIndex].ranges
                );
            }
            // Otherwise, combine style for each column of the range separately
            else {
                rows.forEach((styles, columnIndex) => {
                    const { x, y } = { x: coords.x + columnIndex, y: coords.y };
                    const { text: columnAddress } =
                        this.ac.getAddressFromCoords(x, y);
                    const columnRange = `${columnAddress}:${columnAddress}`;

                    let stylesIndex = findIndex(finalCombinedStyles, {
                        styles,
                    });
                    if (stylesIndex === -1) {
                        finalCombinedStyles.push({
                            styles,
                            ranges: [columnRange],
                        });
                        stylesIndex = finalCombinedStyles.length - 1;
                    } else {
                        finalCombinedStyles[stylesIndex].ranges.push(
                            columnRange
                        );
                    }

                    // finalCombinedStyles[stylesIndex].ranges = uniq(
                    //     finalCombinedStyles[stylesIndex].ranges
                    // );
                });
            }
        });

        return finalCombinedStyles;
    };



    getAppliedStyles = async (savedStyles: Array<{ address: string, format: Excel.RangeFormat }>) => {
        // Create an array to store cell formats
        const cellFormats: Array<{ address: string, format: Excel.RangeFormat }> = savedStyles;


        const value = sessionStorage.getItem('isGettingRendered');


        this.isGettingRendered = true;

        try {


        } catch (e) {
            console.log('Rendering session:', e);
        } finally {
            this.isGettingRendered = false;
            sessionStorage.setItem('isGettingRendered', "false");
        }
        return cellFormats;

    };
    applySavedStyles = async (savedStyles: Array<{ address: string, format: Excel.RangeFormat }>) => {
        // apply styles to the sheet
        try {

            await Excel.run(async (context) => {
                let sheet = context.workbook.worksheets.getItemOrNullObject("Report");
                let app = context.workbook.application;
                await context.sync();

                if (!sheet.isNullObject) {
                    const usedRange = sheet.getUsedRangeOrNullObject();
                    await context.sync();

                    if (!usedRange.isNullObject) {
                        usedRange.load("address, rowCount, columnCount");
                        await context.sync();

                        const rowCount = usedRange.rowCount;
                        const columnCount = usedRange.columnCount;

                        const cells = [];
                        for (let i = 0; i < rowCount; i++) {
                            for (let j = 0; j < columnCount; j++) {
                                const cell = usedRange.getCell(i, j);
                                cell.load(["address", "format"]);
                                cells.push(cell);
                            }
                        }
                        await context.sync();

                        const propertiesToLoadInFormat = [
                            'fill', 'font', 'borders', 'columnWidth', 'horizontalAlignment',
                            'indentLevel', 'rowHeight', 'verticalAlignment', 'wrapText'
                        ];

                        for (const cell of cells) {
                            cell.format.load(propertiesToLoadInFormat);
                        }
                        await context.sync();
                        app.suspendScreenUpdatingUntilNextSync();
                        const changeBorder = (sourceBorder: Excel.RangeBorderCollection, destinationBorder: Excel.RangeBorder[], sideIndex: Excel.BorderIndex) => {
                            const border = sourceBorder.items.find((c) => c.sideIndex == sideIndex);
                            if (border) {
                                const destBorder = destinationBorder.find(x => x.sideIndex === sideIndex);
                                if (destBorder) {
                                    border.style = destBorder.style;
                                    border.weight = destBorder.weight;
                                    border.color = destBorder?.color;
                                }
                            }
                        };

                        for (const cell of cells) {
                            const cellAddress = cell.address;
                            const cellFormat = cell.format;
                            const cellStyles = savedStyles.filter(x => x.address === cellAddress);
                            let cellStyle1: {};

                            cellStyles.forEach((cell, index) => {
                                if (index > 0) {
                                    cellStyle1 = Object.assign(cellStyle1, cell.format);
                                } else {
                                    cellStyle1 = cell.format;
                                }
                            });
                            if (cellStyle1) {
                                const cellStyle = cellStyle1 as Excel.RangeFormat;
                                if (!cellStyle.fill?.isNullObject) {
                                    cellFormat.fill.color = cellStyle.fill.color;
                                }
                                if (!cellStyle.font?.isNullObject) {
                                    Object.assign(cellFormat.font, {
                                        bold: cellStyle.font?.bold,
                                        color: cellStyle.font?.color,
                                        italic: cellStyle.font?.italic,
                                        name: cellStyle.font?.name,
                                        size: cellStyle.font?.size,
                                        underline: cellStyle.font?.underline
                                    });
                                }
                                if (!cellStyle.borders?.isNullObject && !Array.isArray(cellStyle.borders)) {
                                    const borderItems = cellStyle.borders.items;
                                    if (!isEmpty(borderItems)) {
                                        for (const sideIndex of [Excel.BorderIndex.edgeBottom, Excel.BorderIndex.edgeTop, Excel.BorderIndex.edgeLeft, Excel.BorderIndex.edgeRight]) {
                                            changeBorder(cellFormat.borders, borderItems, sideIndex);
                                        }
                                    }
                                }
                                if (cellStyle?.columnWidth != null) {
                                    cellFormat.columnWidth = cellStyle.columnWidth;
                                }
                                if (cellStyle?.rowHeight != null) {
                                    cellFormat.rowHeight = cellStyle.rowHeight;
                                }
                                if (cellStyle?.indentLevel != null) {
                                    cellFormat.indentLevel = cellStyle.indentLevel;
                                }
                                if (cellStyle?.horizontalAlignment != null) {
                                    cellFormat.horizontalAlignment = cellStyle.horizontalAlignment;
                                }
                                if (cellStyle?.verticalAlignment != null) {
                                    cellFormat.verticalAlignment = cellStyle.verticalAlignment;
                                }
                                if (cellStyle?.wrapText != null) {
                                    cellFormat.wrapText = cellStyle.wrapText;
                                }
                            }
                        }
                    }
                    await context.sync();
                }
            });

        } catch (e) {
            console.log(e);
        }
    }
    setUpEvents = async (onWorkSheetFormatChanged: (event: Excel.WorksheetFormatChangedEventArgs) => Promise<any>) => {
        try {
            await Excel.run(async (context) => {
                let sheet = context.workbook.worksheets.getItemOrNullObject("Report");
                await context.sync();
                if (!sheet.isNullObject) {
                    sheet.onFormatChanged.add(onWorkSheetFormatChanged);
                    await context.sync()
                }
            });
        }
        catch (e) {
            console.log(e);
        }

    }

}
